import * as yup from 'yup';

import { useMemo } from 'react';

import useAccessControl from 'hooks/useAccessControl';

import { PLATFORM } from 'utils/constants';

const useSalesChannelSchema = (salesChannelType, whRequired = false) => {
	// Common schema fields that are shared across multiple forms
	const isAuthorized = useAccessControl();

	const commonFields = useMemo(
		() => ({
			storeName: yup.string().nullable().required(),
			active: yup.boolean(),
			prefix: yup.string().nullable(),
			warehouseID: yup.number().nullable(),
			baseUrl: yup.string().nullable().url()
		}),
		[]
	);

	// Common default values shared across forms
	const commonDefaults = useMemo(
		() => ({
			storeName: null,
			active: false,
			prefix: null,
			warehouseID: null,
			baseUrl: null
		}),
		[]
	);

	// Common advanced fields
	const advancedFields = useMemo(
		() => ({
			addHour: yup.number().min(-12).max(12).nullable(),
			statuses: yup.array().nullable(),
			codString: yup.string().nullable(),
			ignoreNonCodOnPending: yup.string().nullable(),
			commentKeys: yup.string().nullable(),
			TrackingUrlType: yup.string().nullable(),
			filterByTag: yup.string().nullable()
		}),
		[]
	);

	// Common advanced defaults
	const advancedDefaults = useMemo(
		() => ({
			addHour: null,
			statuses: null,
			codString: null,
			ignoreNonCodOnPending: null,
			commentKeys: null,
			TrackingUrlType: null,
			filterByTag: null
		}),
		[]
	);

	// Common developer fields
	const developerFields = useMemo(
		() => ({
			secret: yup.string().nullable(),
			token: yup.string().nullable(),
			apiUrl: yup.string().nullable(),
			productUrl: yup.string().nullable(),
			paymentNeedConfirm: yup.string().nullable(),
			webhookStatus: yup.boolean(),
			lastUpdateDate: yup.date().nullable(),
			productUpdateDate: yup.date().nullable(),
			username: yup.string().nullable(),
			password: yup.string().nullable(),
			ignoreRecentOrders: yup.number().nullable(),
			excludePaymentMethods: yup.string().nullable(),
			fullyPaid: yup.string().nullable(),
			fullyNOTPaid: yup.string().nullable(),
			statusNeedConfirm: yup.string().nullable(),
			statusOnhold: yup.string().nullable(),
			cityLabel: yup.string().nullable(),
			districtLabel: yup.string().nullable(),
			assignToWarehouse: yup.boolean(),
			metadata: yup.string().nullable().json()
		}),
		[]
	);

	// Common developer defaults
	const developerDefaults = useMemo(
		() => ({
			secret: null,
			token: null,
			apiUrl: null,
			productUrl: null,
			paymentNeedConfirm: null,
			webhookStatus: false,
			lastUpdateDate: null,
			productUpdateDate: null,
			username: null,
			password: null
		}),
		[]
	);

	// Combined schemas and default values for each sales channel
	const salesChannels = useMemo(
		() => ({
			IkasForm: {
				schema: yup.object().shape({
					...commonFields,
					...advancedFields,
					...developerFields,
					baseUrl: yup.string().url().nullable().required(),
					username: yup.string().nullable(),
					password: yup.string().nullable()
				}),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					codString: 'Cash on Delivery (COD)'
				}
			},
			AkinonForm: {
				schema: yup
					.object()
					.shape({
						...commonFields
					})
					.required(),
				defaultValues: {
					...commonDefaults
				}
			},
			NoonForm: {
				schema: yup.object().shape({
					storeName: yup.string().nullable().required(),
					secret: yup.string().nullable().required()
				}),
				defaultValues: {
					storeName: null,
					secret: null
				}
			},
			BigcommerceForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults
				}
			},
			ChannelEngineForm: {
				schema: yup
					.object()
					.shape({
						storeName: yup.string().nullable().required(),
						baseUrl: yup.string().nullable().url().required(),
						token: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					storeName: null,
					baseUrl: null,
					token: null
				}
			},
			RetailerCredentialsForm: {
				schema: yup.object().shape({
					...commonFields,
					warehouseID:
						isAuthorized('MultiWarehouseModule') && whRequired
							? yup.string().nullable().required()
							: yup.string().nullable()
				}),
				defaultValues: {
					...commonDefaults,
					warehouseID: null
				}
			},
			MagentoForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required(),
						token: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					statuses: ['pending', 'processing'],
					codString: 'cashondelivery',
					ignoreNonCodOnPending: 'pending'
				}
			},

			FoodicsForm: {
				schema: yup.object().shape({
					storeName: yup.string().nullable().required(),
					username: yup.string().nullable().required(),
					token: yup.string().nullable().required(),
					statuses: yup.array().nullable()
				}),
				defaultValues: {
					storeName: null,
					username: null,
					token: null,
					statuses: null
				}
			},

			PrestashopForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required(),
						token: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					statuses: ['pending', 'processing'],
					codString: 'cashondelivery',
					ignoreNonCodOnPending: 'pending'
				}
			},

			SalesforceForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						password: yup.string().nullable().required(),
						secret: yup.string().nullable().required(),
						username: yup.string().nullable().required(),
						token: yup.string().nullable().required(),
						baseUrl: yup.string().url().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					baseUrl: 'https://{{shortCode}}.api.commercecloud.salesforce.com',
					password: null,
					secret: null,
					username: null,
					token: null
				}
			},

			ShopifyForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required(),
						token: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					codString: 'Cash on Delivery (COD)'
				}
			},

			ShahbandrForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required(),
						token: yup.string().nullable().required(),
						secret: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					statuses: ['processing'],
					codString: 'cod',
					ignoreNonCodOnPending: 'pending'
				}
			},

			TicimaxForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required(),
						token: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					codString: 'Cash on Delivery (COD)'
				}
			},
			FatherShopsForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					codString: 'Cash on Delivery (COD)'
				}
			},

			TrendyolForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						secret: yup.string().nullable().required(),
						username: yup.string().nullable().required(),
						password: yup.string().nullable().required(),
						statuses: yup.array().nullable()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					username: null,
					ignoreNonCodOnPending: 'pending'
				}
			},

			WoocommerceForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required(),
						token: yup.string().nullable().required(),
						secret: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					statuses: ['processing'],
					codString: 'cod',
					ignoreNonCodOnPending: 'pending'
				}
			},

			ZohoForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						username: yup.string().nullable().required(),
						password: yup.string().nullable().required(),
						secret: yup.string().nullable().required(),
						statuses: yup.array().nullable(),
						ignoreNonCodOnPending: yup.string().nullable()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					username: null,
					password: null,
					secret: null,
					ignoreNonCodOnPending: 'pending'
				}
			},

			TsoftForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required(),
						username: yup.string().nullable().required(),
						password: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					codString: 'Cash on Delivery (COD)',
					deliveryOptionFilter: []
				}
			},

			ZidForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						username: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					statuses: ['ready']
				}
			},
			PikForm: {
				schema: yup.object().shape({
					storeName: yup.string().nullable().required(),
					username: yup.string().nullable().required(),
					password: yup.string().nullable().required(),
					secret: yup.string().nullable().required(),
					baseUrl: yup.string().nullable().required()
				}),
				defaultValues: {
					storeName: null,
					username: null,
					password: null,
					secret: null,
					baseUrl: null
				}
			},
			JahezForm: {
				schema: yup.object().shape({
					storeName: yup.string().nullable().required(),
					password: yup.string().nullable().required(),
					secret: yup.string().nullable().required()
				}),
				defaultValues: {
					storeName: null,
					password: null,
					secret: null
				}
			},
			SyliusForm: {
				schema: yup
					.object()
					.shape({
						...commonFields,
						...advancedFields,
						...developerFields,
						baseUrl: yup.string().url().nullable().required(),
						username: yup.string().nullable().required(),
						password: yup.string().nullable().required()
					})
					.required(),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					codString: 'Cash on Delivery (COD)'
				}
			},
			OpenCartForm: {
				schema: yup.object().shape({
					...commonFields,
					...advancedFields,
					...developerFields,
					baseUrl: yup.string().url().nullable().required(),
					secret: yup.string().nullable()
				}),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					codString: 'cod'
				}
			},

			StoreBasicConfigForm: {
				schema: yup.object().shape({
					...commonFields
				}),
				defaultValues: {
					...commonDefaults
				}
			},
			SallaForm: {
				schema: yup.object().shape({
					...commonFields,
					...advancedFields,
					...developerFields
				}),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults
				}
			},
			SallaWebhookForm: {
				schema: yup.object().shape({
					...commonFields,
					...advancedFields,
					...developerFields,
					apiUrl: yup.string().nullable(),
					statuses: yup.array().nullable()
				}),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					apiUrl: null,
					statuses: null
				}
			},
			SallaPrivateAppForm: {
				schema: yup.object().shape({
					...commonFields,
					...advancedFields,
					...developerFields,
					token: yup.string().nullable().required(),
					productUrl: yup.string().nullable().required(),
					apiUrl: yup.string().nullable(),
					username: yup.string().nullable()
				}),
				defaultValues: {
					...commonDefaults,
					...advancedDefaults,
					...developerDefaults,
					token: null,
					productUrl: null,
					apiUrl: null,
					username: null
				}
			},
			SallaAutomationForm: {
				schema: yup.object().shape({
					...advancedFields,
					...developerFields,
					storeName: yup.string().nullable().required(),
					active: yup.boolean(),
					prefix: yup.string().nullable(),
					merchantId: yup
						.string()
						.nullable()
						.when([], {
							is: () => import.meta.env.VITE_PLATFORM === PLATFORM.desktop,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					username: yup
						.string()
						.nullable()
						.when([], {
							is: () => import.meta.env.VITE_PLATFORM === PLATFORM.web,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					secret: yup
						.string()
						.nullable()
						.when([], {
							is: () => import.meta.env.VITE_PLATFORM === PLATFORM.web,
							then: (schema) => schema.required(),
							otherwise: (schema) => schema
						}),
					password: yup.string().nullable().max(4),
					apiUrl: yup.string().nullable(),
					statuses: yup.array().nullable(),
					syncInterval: yup.number().nullable(),
					credentialsApproval: yup.bool().oneOf([true], 'fieldMustBeChecked')
				}),

				defaultValues: {
					...advancedDefaults,
					...developerDefaults,
					storeName: null,
					active: true,
					prefix: null,
					username: null,
					secret: null,
					password: null,
					apiUrl: null,
					statuses: null,
					syncInterval: 5,
					credentialsApproval: false
				}
			},
			MiraklForm: {
				schema: yup
					.object()
					.shape({
						storeName: yup.string().nullable().required(),
						token: yup.string().nullable().required(),
						baseUrl: yup.string().url().nullable().required()
					})
					.required(),
				defaultValues: {
					storeName: null,
					token: null,
					baseUrl: null
				}
			}
		}),
		[commonFields, advancedFields, developerFields, commonDefaults, advancedDefaults, developerDefaults]
	);

	const channelSchema = useMemo(
		() => ({
			schema: salesChannels[salesChannelType]?.schema,
			defaultValues: salesChannels[salesChannelType]?.defaultValues
		}),
		[salesChannels, salesChannelType]
	);

	if (salesChannelType) {
		return channelSchema;
	}

	return {
		schema: salesChannels.schemas,
		defaultValues: salesChannels.defaultValues,
		salesChannels
	};
};

export default useSalesChannelSchema;
